import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";

import { isApiError } from "config/axios-utils";

import { clientConfigurationKeys } from "hooks/api/query-keys";

import { getClientConfiguration } from "generated/api/client-configuration";
import type {
  ClientConfigurationDTO,
  ClientConfigurationDTOConfigKey,
} from "generated/schemas";

const configApi = getClientConfiguration();

type ConfigKey = ClientConfigurationDTOConfigKey | string;

export type ClientConfigurationValue = {
  configValue: string;
};

type UseQueryHookOptions<TQueryFnData, TData = TQueryFnData> = Omit<
  UseQueryOptions<TQueryFnData, Error, TData>,
  "queryKey" | "queryFn"
>;

/**
 * Resolve a configuration value, treating 404 as "not set" (legacy behaviour
 * inherited from `Client.getClientConfiguration` / `getSystemConfiguration`).
 */
async function fetchConfigurationOr404<T extends () => Promise<ClientConfigurationDTO>>(
  fetcher: T,
): Promise<ClientConfigurationValue> {
  try {
    const response = await fetcher();
    return { configValue: response?.configValue || "" };
  } catch (error) {
    if (isApiError(error) && error.response?.status === 404) {
      return { configValue: "" };
    }
    throw error;
  }
}

export const useClientConfiguration = <TData = ClientConfigurationValue>(
  clientId: string,
  configKey: ConfigKey,
  options?: UseQueryHookOptions<ClientConfigurationValue, TData>,
) => {
  return useQuery({
    queryKey: clientConfigurationKeys.client(clientId, configKey),
    queryFn: () =>
      fetchConfigurationOr404(() =>
        configApi.getClientConfiguration({
          clientId,
          configKey: configKey as ClientConfigurationDTOConfigKey,
        }),
      ),
    ...options,
    enabled: Boolean(clientId) && (options?.enabled ?? true),
  });
};

export const useSystemConfiguration = <TData = ClientConfigurationValue>(
  configKey: ConfigKey,
  options?: UseQueryHookOptions<ClientConfigurationValue, TData>,
) => {
  return useQuery({
    queryKey: clientConfigurationKeys.system(configKey),
    queryFn: () =>
      fetchConfigurationOr404(() =>
        configApi.getSystemConfiguration({
          configKey: configKey as ClientConfigurationDTOConfigKey,
        }),
      ),
    ...options,
  });
};

export const useSetSystemConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ configKey, configValue }: { configKey: ConfigKey; configValue: string }) =>
      configApi.createOrUpdateSystemConfiguration(
        { configKey: configKey as ClientConfigurationDTOConfigKey },
        { configValue },
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: clientConfigurationKeys.system(variables.configKey),
      });
    },
  });
};

export const useSetClientConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      configKey,
      configValue,
    }: {
      clientId: string;
      configKey: ConfigKey;
      configValue: string;
    }) =>
      configApi.createOrUpdateClientConfiguration(
        { clientId, configKey: configKey as ClientConfigurationDTOConfigKey },
        { configValue },
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: clientConfigurationKeys.client(variables.clientId, variables.configKey),
      });
    },
  });
};

export const useDeleteClientConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, configKey }: { clientId: string; configKey: ConfigKey }) =>
      configApi.deleteClientConfiguration({
        clientId,
        configKey: configKey as ClientConfigurationDTOConfigKey,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: clientConfigurationKeys.client(variables.clientId, variables.configKey),
      });
    },
  });
};
